<script lang="ts">
/**
 * AddressBar component
 * This component is used to display the address bar for the operation block
 * It is used to display the path, method, server, and history for the operation
 */
export default {
  name: 'AddressBar',
}
export type AddressBarProps = {
  /** An external example must finish loading before sending. */
  executionDisabled?: boolean
  /** Current request path */
  path: string
  /** Current request method */
  method: string
  /** Whether the request target belongs to an OpenAPI webhook. */
  isWebhook?: boolean
  /** Openapi document slug */
  documentSlug: string
  /** Currently selected example key for the current operation */
  exampleKey: string
  /** Currently selected server */
  server: ServerObject | null
  /** Server list available for operation/document */
  servers: ServerObject[]
  /** List of request history */
  history: History[]
  /** Client layout */
  layout: ClientLayout
  /** Event bus */
  eventBus: WorkspaceEventBus
  /** Environment */
  environment: XScalarEnvironment
  /** Meta information for the server */
  serverMeta: ServerMeta
}
</script>
<script setup lang="ts">
import { ScalarButton } from '@scalar/components/button'
import { ScalarIcon } from '@scalar/components/icon'
import { getSelector } from '@scalar/helpers/dom/get-selector'
import { getHttpMethodInfo } from '@scalar/helpers/http/http-info'
import { extractServerFromPath } from '@scalar/helpers/url/extract-server-from-path'
import { ScalarIconCopy, ScalarIconWarningCircle } from '@scalar/icons'
import { EditorView } from '@scalar/use-codemirror'
import type {
  ApiReferenceEvents,
  ServerMeta,
  WorkspaceEventBus,
} from '@scalar/workspace-store/events'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import type { ServerObject } from '@scalar/workspace-store/schemas/v3.2/strict/openapi-document'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  useTemplateRef,
  watch,
} from 'vue'

import { HttpMethod } from '@/components/HttpMethod'
import { getOperationExampleKey } from '@/v2/blocks/operation-block/helpers/response-cache'
import { isPlaceholderPath } from '@/v2/blocks/scalar-address-bar-block/helpers/is-placeholder-path'
import { refocusBlurTarget } from '@/v2/blocks/scalar-address-bar-block/helpers/refocus-blur-target'
import { useLoadingAnimation } from '@/v2/blocks/scalar-address-bar-block/hooks/use-loading-animation'
import { usePathMasking } from '@/v2/blocks/scalar-address-bar-block/hooks/use-path-masking'
import { CodeInput } from '@/v2/components/code-input'
import { ServerDropdown } from '@/v2/components/server'
import { useLocalization } from '@/v2/features/localization'
import type { ClientLayout } from '@/v2/types/layout'

import AddressBarHistory, { type History } from './AddressBarHistory.vue'

const {
  path,
  method,
  layout,
  eventBus,
  exampleKey,
  documentSlug,
  server,
  servers,
  environment,
  serverMeta,
  isWebhook = false,
} = defineProps<AddressBarProps>()

const emit = defineEmits<{
  /** Execute the current operation example */
  (e: 'execute'): void
  /** Select a request history item by index */
  (e: 'select:history:item', payload: { index: number }): void
  /** Update the full destination URL used to deliver a webhook. */
  (e: 'update:webhook-url', url: string): void
}>()

const { translate } = useLocalization()

// ───────────────────────────────────────────────────────────────────
// Template refs & reactive state
// ───────────────────────────────────────────────────────────────────

const id = useId()
const sendButtonRef = useTemplateRef('sendButtonRef')
const mobileSendButtonRef = useTemplateRef('mobileSendButtonRef')
const addressBarRef = useTemplateRef('addressBarRef')

const { percentage, startLoading, stopLoading, isLoading } =
  useLoadingAnimation()

const pathConflict = ref<string | null>(null)
const methodConflict = ref<string | null>(null)
const tabbedOut = ref(false)
const isServerDropdownOpen = ref(false)
const isHistoryDropdownOpen = ref(false)

// ───────────────────────────────────────────────────────────────────
// Derived state
// ───────────────────────────────────────────────────────────────────

/** Keeps the cursor visible past the fade-right overlay while typing */
const addressBarScrollMargins = EditorView.scrollMargins.of(() => ({
  right: 24,
}))

/** Animated background transform for the loading indicator */
const style = computed(() => ({
  backgroundColor: `color-mix(in srgb, transparent 90%, ${getHttpMethodInfo(method).colorVar})`,
  transform: `translate3d(-${percentage.value}%,0,0)`,
}))

/** Whether there is a path or method conflict */
const hasConflict = computed(() => methodConflict.value || pathConflict.value)

/**
 * Placeholder for the path field.
 *
 * Webhooks have no server to prefix the path, so the field doubles as the full
 * destination URL. Hint that a complete URL (including the host) is expected,
 * rather than a bare path.
 */
const pathPlaceholder = computed(() => {
  if (server) {
    return ''
  }
  return isWebhook
    ? translate('apiClient.addressBar.webhookUrlPlaceholder')
    : translate('apiClient.addressBar.urlPlaceholder')
})

/** Whether either dropdown (server or history) is open */
const isDropdownOpen = computed(
  () => isServerDropdownOpen.value || isHistoryDropdownOpen.value,
)

/** Uniquely identifies the currently selected operation + example */
const uniqueKey = computed(() =>
  getOperationExampleKey(method, path, exampleKey, documentSlug),
)

/** Clear conflict state when switching to a different operation */
watch(uniqueKey, () => {
  pathConflict.value = null
  methodConflict.value = null
})

// ───────────────────────────────────────────────────────────────────
// Focus helpers
// ───────────────────────────────────────────────────────────────────

const handleFocusSendButton = (): void => {
  const desktop = sendButtonRef.value?.$el
  const mobile = mobileSendButtonRef.value?.$el

  // Focus whichever send button is currently visible
  if (desktop && desktop.offsetParent !== null) {
    desktop.focus()
  } else {
    mobile?.focus()
  }
}

const handleFocusAddressBar = (
  payload: ApiReferenceEvents['ui:focus:address-bar'],
): void => {
  // On non-desktop layouts, if already focused we let the browser handle the
  // native behavior (e.g. selecting the browser's own address bar).
  if (addressBarRef.value?.isFocused && layout !== 'desktop') {
    return
  }

  addressBarRef.value?.focus('end')

  if (payload && 'clear' in payload && payload.clear) {
    addressBarRef.value?.setCodeMirrorContent('')
  }

  if (payload && 'event' in payload) {
    payload.event.preventDefault()
  }
}

// ───────────────────────────────────────────────────────────────────
// Path masking
//
// Drafts on `/` and auto-generated `/_scalar_temp...` paths are internal
// placeholders — the address bar focuses and clears on mount and on
// navigation so the user sees a blank prompt instead of the internal
// path. The deferred mask only clears when CodeMirror still contains that
// placeholder path, so a user edit that lands before the frame is preserved.
// ───────────────────────────────────────────────────────────────────

usePathMasking({
  isReady: () => addressBarRef.value?.codeMirror,
  operationKey: () => uniqueKey.value,
  shouldMask: () => isPlaceholderPath(path, documentSlug),
  // Defer to the next frame so focus() runs after click-handler side
  // effects that move focus (e.g. a dropdown refocusing its trigger),
  // which would otherwise blur our input and emit a spurious path
  // update against the now-empty value.
  onMask: () =>
    requestAnimationFrame(() => {
      const editorContent =
        addressBarRef.value?.codeMirror?.state.doc.toString()

      if (editorContent && editorContent !== path) {
        return
      }

      handleFocusAddressBar({ clear: true })
    }),
})

// ───────────────────────────────────────────────────────────────────
// Server extraction
// ───────────────────────────────────────────────────────────────────

/** If the path contains a server URL, extract it and select or add that server */
const extractAndSelectServer = (targetPath: string): string => {
  const extracted = extractServerFromPath(targetPath)
  if (!extracted) {
    return targetPath
  }

  const [url, remainingPath] = extracted

  // Server is already selected — nothing to do
  if (url === server?.url) {
    return remainingPath
  }

  const matchingServer = servers.find((s) => s.url === url)
  if (matchingServer) {
    eventBus.emit('server:update:selected', { url, meta: serverMeta })
  } else {
    eventBus.emit('server:add:server', {
      url,
      select: true,
      meta: { type: 'operation', path, method },
    })
  }

  return remainingPath
}

// ───────────────────────────────────────────────────────────────────
// Path / method updates
// ───────────────────────────────────────────────────────────────────

const normalizePath = (value: string): string =>
  value.startsWith('/') ? value : `/${value}`

/** Emit a path/method update and reconcile conflicts + cursor state on the result */
const emitPathMethodUpdate = (
  targetMethod: string,
  targetPath: string,
  blurTargetSelector: string | null = null,
): void => {
  if (isWebhook) {
    // A webhook has no OpenAPI server, so the field holds the full destination
    // URL. Keep exactly what the user entered — do not split it into an invisible
    // server plus a leftover path, and do not force a leading slash. That split
    // is what made a typed URL collapse to a stray "/" on blur.
    const webhookUrl = targetPath.trim()
    addressBarRef.value?.setCodeMirrorContent(webhookUrl)
    emit('update:webhook-url', webhookUrl)
    methodConflict.value = null
    pathConflict.value = null
    nextTick(() => refocusBlurTarget(blurTargetSelector))
    return
  }

  const extractedPath = extractAndSelectServer(targetPath)
  const normalizedPath = normalizePath(extractedPath)

  // Keep CodeMirror in sync so a conflict does not leave a stale value on screen
  addressBarRef.value?.setCodeMirrorContent(normalizedPath)

  eventBus.emit('operation:update:pathMethod', {
    meta: { method, path },
    blurTargetSelector,
    payload: { method: targetMethod, path: normalizedPath },
    callback: (status, returnedSelector) => {
      if (status === 'success' || status === 'no-change') {
        methodConflict.value = null
        pathConflict.value = null
      } else if (status === 'conflict') {
        if (targetMethod !== method) {
          methodConflict.value = targetMethod
        }
        if (normalizedPath !== path) {
          pathConflict.value = normalizedPath
        }

        return
      }

      // Edge case: pasting a full URL extracts the server but leaves the path
      // unchanged. CodeMirror still shows the full URL, so force it back to
      // just the path.
      const mirrorContent = addressBarRef.value?.codeMirrorRef?.textContent
      if (
        status === 'no-change' &&
        mirrorContent &&
        mirrorContent !== extractedPath
      ) {
        addressBarRef.value?.setCodeMirrorContent(extractedPath)
      }

      nextTick(() => refocusBlurTarget(returnedSelector))
    },
  })
}

/** Change the operation's method, preferring the conflicting path if present */
const handleMethodChange = (newMethod: string): void =>
  emitPathMethodUpdate(newMethod, pathConflict.value ?? path)

/**
 * Save the path on blur and replay the click that caused the blur (so e.g. a
 * click on the Send button still fires after the async update resolves).
 *
 * Tab-outs do not replay — tabbing to a button should not trigger its click
 * (`tabbedOut` is set on `@keydown.tab`).
 *
 * Programmatic focus moves also do not replay. On first navigation into a
 * draft operation, `usePathMasking` focuses and clears the address bar while
 * `ScalarSidebarNestedItems` focuses the drilled-in Back button in
 * `nextTick`. That steals focus from CodeMirror, fires blur with a
 * `relatedTarget` (the Back button), and without a guard we would treat it
 * like a user click: capture the selector, then `refocusBlurTarget` would
 * synthesize `.click()` on Back and navigate away.
 *
 * `FocusEvent.sourceCapabilities` is null when no input device caused the
 * focus change (e.g. `element.focus()`). A real pointer click sets it on the
 * blur that precedes the click, so we still replay Send and other buttons.
 */
const handlePathBlur = (newPath: string, event: FocusEvent): void => {
  const relatedTarget =
    event.relatedTarget instanceof Element ? event.relatedTarget : null
  const blurTargetSelector =
    tabbedOut.value ||
    ('sourceCapabilities' in event && event.sourceCapabilities === null)
      ? null
      : getSelector(relatedTarget)
  tabbedOut.value = false

  emitPathMethodUpdate(
    methodConflict.value ?? method,
    newPath,
    blurTargetSelector,
  )
}

/** Save the path on Enter and trigger the Send button after the update resolves */
const handlePathSubmit = (
  newPath: string,
  event: KeyboardEvent | FocusEvent,
): void => {
  // Prevent the global hotkey listener from also handling this Enter press
  event.stopPropagation()

  emitPathMethodUpdate(
    methodConflict.value ?? method,
    newPath,
    '[data-addressbar-action="send"]',
  )
}

/** Unset the server when backspace is pressed on an empty path */
const handlePathBackspace = (event: KeyboardEvent): void => {
  if (event.target instanceof HTMLElement && event.target.innerText === '\n') {
    // A webhook has no server to unset; its full URL lives in the field itself.
    if (isWebhook) {
      return
    }
    eventBus.emit('server:update:selected', { url: '', meta: serverMeta })
  }
}

const handleServerSelect = (
  payload: ApiReferenceEvents['server:update:selected'],
): void => {
  eventBus.emit('server:update:selected', payload)
}

const handleServerVariable = (
  payload: ApiReferenceEvents['server:update:variables'],
): void => {
  eventBus.emit('server:update:variables', payload)
}

/** Address bar copy is handled in OperationBlock (same URL as Send). */
const requestCopyUrl = (): void => {
  eventBus.emit('copy-url:address-bar')
}

// ───────────────────────────────────────────────────────────────────
// Navigation
// ───────────────────────────────────────────────────────────────────

const navigateToServersPage = (): void => {
  if (serverMeta.type === 'operation') {
    eventBus.emit('ui:navigate', {
      page: 'operation',
      path: 'servers',
      operationPath: serverMeta.path,
      method: serverMeta.method,
    })
    return
  }

  eventBus.emit('ui:navigate', { page: 'document', path: 'servers' })
}

// ───────────────────────────────────────────────────────────────────
// Lifecycle
// ───────────────────────────────────────────────────────────────────

const unsubscribes: Array<() => void> = []

onMounted(() => {
  unsubscribes.push(
    eventBus.on('ui:focus:address-bar', handleFocusAddressBar),
    eventBus.on('ui:focus:send-button', handleFocusSendButton),
    eventBus.on('hooks:on:request:sent', startLoading),
    eventBus.on('hooks:on:request:complete', stopLoading),
  )
})

onBeforeUnmount(() => {
  for (const unsubscribe of unsubscribes) {
    unsubscribe()
  }

  // Prevents the loading animation from continuing after unmount
  stopLoading()
})

defineExpose({
  methodConflict,
  pathConflict,
})
</script>
<template>
  <!--
    Address bar.

    The wide-container layout matches the original single-row bar:
    `[Method | URL | Copy | History | Send]`. When the surrounding
    `@container` drops below `@3xl` the bar collapses to
    `[URL | History]` and a second row appears beneath it with a
    duplicate `Method`, `Copy`, and `Send` so the URL gets the full
    container width while every action stays on the same line as the
    send button.
  -->
  <div
    class="order-last flex h-auto w-full max-w-3xl grow-3 flex-wrap items-stretch [--scalar-address-bar-height:32px] @3xl:order-0 @3xl:flex-nowrap">
    <!-- Address Bar -->
    <div
      :id="id"
      class="address-bar-bg-states text-xxs group relative flex h-(--scalar-address-bar-height) w-full flex-1 flex-row items-stretch rounded-lg p-0.75"
      :class="{
        'outline-c-danger outline': hasConflict,
        'rounded-b-none': isDropdownOpen,
      }">
      <div
        class="pointer-events-none absolute top-0 left-0 block h-full w-full overflow-hidden rounded-lg border"
        :class="{
          'rounded-b-none': isDropdownOpen,
        }">
        <div
          class="absolute top-0 left-0 h-full w-full"
          :style />
      </div>

      <!--
        Method, Copy, and Send are hidden in mobile mode (container
        narrower than `@3xl`) and the duplicate buttons in the trailing
        mobile actions row take over at that point.
      -->
      <div class="hidden @3xl:flex">
        <HttpMethod
          :isEditable="layout !== 'modal' && !isWebhook"
          isSquare
          :method="methodConflict ?? method"
          teleport
          @change="handleMethodChange" />
      </div>

      <div
        class="scroll-timeline-x scroll-timeline-x-hidden relative flex w-full bg-blend-normal"
        dir="ltr">
        <!-- Servers -->
        <ServerDropdown
          v-if="servers.length"
          :layout="isWebhook ? 'modal' : layout"
          :meta="serverMeta"
          :server="server"
          :servers="servers"
          :target="id"
          @update:open="(value) => (isServerDropdownOpen = value)"
          @update:selectedServer="handleServerSelect"
          @update:servers="navigateToServersPage"
          @update:variable="handleServerVariable" />

        <!-- Path + URL + env vars -->
        <CodeInput
          ref="addressBarRef"
          alwaysEmitChange
          :aria-label="translate('apiClient.addressBar.path')"
          class="ml-1 min-w-fit pl-px outline-none"
          disableCloseBrackets
          :disabled="layout === 'modal' && !isWebhook"
          disableEnter
          disableTabIndent
          :emitOnBlur="false"
          :environment="environment"
          :extensions="[addressBarScrollMargins]"
          importCurl
          :layout="layout"
          :modelValue="path"
          :placeholder="pathPlaceholder"
          server
          @blur="handlePathBlur"
          @keydown.delete="handlePathBackspace"
          @keydown.tab="tabbedOut = true"
          @submit="handlePathSubmit" />
      </div>

      <!-- Copy url button -->
      <ScalarButton
        class="hover:bg-b-3 mx-1 hidden @3xl:flex"
        size="xs"
        variant="ghost"
        @click="requestCopyUrl">
        <ScalarIconCopy />
        <span class="sr-only">{{
          translate('apiClient.addressBar.copyUrl')
        }}</span>
      </ScalarButton>

      <AddressBarHistory
        :history="history"
        :target="id"
        @select:history:item="(payload) => emit('select:history:item', payload)"
        @update:open="(value) => (isHistoryDropdownOpen = value)" />
      <!-- Error message -->
      <div
        v-if="hasConflict"
        class="z-context absolute inset-x-0 top-[calc(100%+4px)] flex flex-col items-center rounded px-6">
        <div
          class="text-c-danger bg-b-danger border-c-danger flex items-center gap-1 rounded border p-1">
          <ScalarIconWarningCircle size="sm" />
          <div class="min-w-0 flex-1">
            {{
              translate('apiClient.addressBar.duplicateRequest', {
                method: (methodConflict ?? method).toUpperCase(),
                path: pathConflict ?? path,
              })
            }}
          </div>
        </div>
      </div>

      <ScalarButton
        ref="sendButtonRef"
        class="relative hidden h-auto shrink-0 overflow-hidden py-1 pr-2.5 pl-2 font-bold @3xl:flex"
        data-addressbar-action="send"
        :disabled="isLoading || executionDisabled"
        @click="emit('execute')">
        <span
          aria-hidden="true"
          class="inline-flex items-center gap-1">
          <ScalarIcon
            class="relative shrink-0 fill-current"
            icon="Play"
            size="xs" />
          <span class="text-xxs flex">{{
            translate('apiClient.addressBar.send')
          }}</span>
        </span>
        <span class="sr-only"
          >{{
            translate('apiClient.addressBar.sendRequest', {
              method,
              url: `${server?.url ?? ''}${path}`,
            })
          }}
        </span>
      </ScalarButton>
    </div>

    <!--
      Mobile actions row. Visible by default and hidden once the
      container reaches `@3xl`, where the duplicate Method / Copy /
      Send buttons move back into the bar itself.
    -->
    <div
      class="mt-2 flex h-(--scalar-address-bar-height) w-full items-stretch gap-1 @3xl:hidden">
      <HttpMethod
        :isEditable="layout !== 'modal' && !isWebhook"
        isSquare
        :method="methodConflict ?? method"
        teleport
        @change="handleMethodChange" />
      <ScalarButton
        class="hover:bg-b-3 ml-auto"
        size="xs"
        variant="ghost"
        @click="requestCopyUrl">
        <ScalarIconCopy />
        <span class="sr-only">{{
          translate('apiClient.addressBar.copyUrl')
        }}</span>
      </ScalarButton>
      <ScalarButton
        ref="mobileSendButtonRef"
        class="relative h-auto shrink-0 overflow-hidden py-1 pr-2.5 pl-2 font-bold"
        data-addressbar-action="send"
        :disabled="isLoading || executionDisabled"
        @click="emit('execute')">
        <span
          aria-hidden="true"
          class="inline-flex items-center gap-1">
          <ScalarIcon
            class="relative shrink-0 fill-current"
            icon="Play"
            size="xs" />
          <span class="text-xxs">{{
            translate('apiClient.addressBar.send')
          }}</span>
        </span>
        <span class="sr-only"
          >{{
            translate('apiClient.addressBar.sendRequest', {
              method,
              url: `${server?.url ?? ''}${path}`,
            })
          }}
        </span>
      </ScalarButton>
    </div>
  </div>
</template>
<style scoped>
:deep(.cm-editor) {
  height: 100%;
  outline: none;
  width: 100%;
}
:deep(.cm-line) {
  padding: 0;
}
:deep(.cm-content) {
  padding: 0;
  display: flex;
  align-items: center;
  font-size: var(--scalar-small);
}
.scroll-timeline-x {
  -ms-overflow-style: none;
  /* Fade the URL at the viewport edges. Mask lives on the scrollport (this
     element) rather than on `.cm-scroller`, whose width follows the URL's
     full content and pushes the right edge off-screen when overflowing. */
  mask-image: linear-gradient(
    to right,
    transparent 0,
    black 6px,
    black calc(100% - 24px),
    transparent 100%
  );
}
.scroll-timeline-x-hidden {
  overflow-x: auto;
}
.scroll-timeline-x-hidden :deep(.cm-scroller) {
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-right: 20px;
  overflow: auto;
}
.scroll-timeline-x-hidden::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
.scroll-timeline-x-hidden :deep(.cm-scroller::-webkit-scrollbar) {
  width: 0;
  height: 0;
  display: none;
}
.scroll-timeline-x-address {
  line-height: 27px;
  scrollbar-width: none; /* Firefox */
}
/* make clickable are to left of send button */
.scroll-timeline-x-address:after {
  content: '';
  position: absolute;
  height: 100%;
  width: 24px;
  right: 0;
  cursor: text;
}

.address-bar-bg-states {
  --scalar-address-bar-bg: color-mix(
    in srgb,
    var(--scalar-background-1),
    var(--scalar-background-2)
  );
  background: var(--scalar-address-bar-bg);
}
.address-bar-bg-states:has(.cm-focused) {
  --scalar-address-bar-bg: var(--scalar-background-1);
  border-color: var(--scalar-border-color);
  outline-width: 1px;
  outline-style: solid;
}
.address-bar-bg-states:has(.cm-focused) .fade-left,
.address-bar-bg-states:has(.cm-focused) .fade-right {
  --scalar-address-bar-bg: var(--scalar-background-1);
}
</style>
